<template>
  <div class="flex flex-col overflow-hidden md:overflow-visible">
    <!-- Header Section -->
    <div v-if="!props.helperCalendar" class="relative">
      <div class="mb-3 flex h-8 items-center">
        <h3
          class="gc-font-title w-full text-center font-secondary font-semibold text-brand md:w-auto md:text-left md:text-dark">
          {{ $t('yourSelection') }}
        </h3>

        <DatesSelection
          class="md:gc-font-title hidden text-base font-semibold md:ml-7 md:block"
          :startDate="startDate"
          :endDate="endDate" />
      </div>
      <IconButton
        v-if="props.showBar"
        class="absolute right-0 top-0 flex h-8 w-8 items-center justify-center border-header-icons text-header-icons md:hover:bg-header-icons"
        :iconClass="'h-4 w-4'"
        @click="toggleViewMode()"
        :icon="isCalendarView ? ChartBarIcon : CalendarDateRange"
        :ariaLabel="
          isCalendarView ? 'Switch to chart view' : 'Switch to calendar view'
        " />
      <hr class="mb-3 hidden h-px w-full border-t-0 bg-dark md:mb-4 md:block" />

      <DatesSelection
        class="text-base font-semibold md:hidden"
        :startDate="startDate"
        :endDate="endDate" />
      <WarningInfo
        class="md:hidden"
        :textClass="'pt-1'"
        :text="$t('calendarRatesInfoNote', { symbol: currencySymbol })" />
    </div>

    <!-- Calendar Section (flex-grow but without scrolling) -->
    <div
      class="flex-grow overflow-y-auto overflow-x-hidden md:overflow-y-visible"
      v-if="isCalendarView"
      @scroll="onCalendarScroll">
      <VueDatePicker
        ref="datepicker"
        :class="{
          dp__vertical_display: isMobile || props.helperCalendar,
          isMobile: isMobile,
        }"
        v-model="selectedDates"
        :multi-calendars="multiCalendarsC"
        auto-apply
        prevent-min-max-navigation
        :hide-offset-dates="!props.helperCalendar"
        week-start="0"
        :month-change-on-scroll="false"
        :day-class="getDayCellClass"
        :inline="{ input: false }"
        :range="{ noDisabledRange: true }"
        :locale="locale"
        :focus-start-date="true"
        :start-date="calendarStartDate"
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        :enable-time-picker="false"
        :day-names="dayNames"
        @update-month-year="handleMonthYear"
        @range-start="onRangeStart"
        @update:model-value="handleDate"
        month-name-format="long"
        :config="{ noSwipe: true }"
        :timezone="{ timezone: timezone }">
        <!-- Custom day and month-year slots -->
        <template #day="{ day, date }">
          <span class="font-secondary text-base font-medium leading-3">
            {{ day }}
          </span>
          <i
            v-if="props.showBar"
            class="gc-font-calendar-numbers font-cal-prices not-italic text-brand">
            {{ getDayRate(date) }}
          </i>
          <div
            class="cal-tooltip cal-start-tooltip"
            v-if="HasTooltip(date, startDate)">
            {{ $t('general.checkIn') }}
          </div>
          <div
            class="cal-tooltip cal-end-tooltip"
            v-if="HasTooltip(date, endDate)">
            {{ $t('general.checkOut') }}
          </div>
        </template>
        <template #month-year="{ month, year, months, handleMonthYearChange }">
          <div
            class="mb-2 flex grow items-center justify-center md:!h-7 md:justify-between md:text-left"
            :class="'cal-' + month + '-' + year">
            <span
              class="gc-font-main-text font-secondary text-base font-medium capitalize">
              {{ months[month].text }} {{ year }}
            </span>
            <div class="hidden md:flex">
              <IconButton
                buttonClass="flex items-center justify-center mr-4 !h-7 !w-7 text-cal-arrows border-cal-arrows md:hover:bg-cal-arrows"
                ariaLabel="left"
                :icon="ChevronIcon"
                :disabled="isNavDisabled(month, year, false)"
                iconClass="h-4 w-4 rotate-180 stroke-3"
                @click="handleMonthYearChange(false)" />
              <IconButton
                buttonClass="flex items-center justify-center !h-7 !w-7 text-cal-arrows border-cal-arrows md:hover:bg-cal-arrows"
                ariaLabel="right"
                :icon="ChevronIcon"
                :disabled="isNavDisabled(month, year, true)"
                iconClass="h-4 w-4 stroke-3"
                @click="handleMonthYearChange(true)" />
            </div>
          </div>
        </template>
      </VueDatePicker>
    </div>

    <div v-else-if="!isCalendarView">
      <!-- Alternative content when not in calendar view -->
      <GraphChart
        :rates="ratesData"
        :startDate="startDate"
        :endDate="endDate"
        :minSelectableDate="minSelectableDate"
        :maxSelectableDate="maxSelectableDate"
        @changeCurrentMonthYear="changeCurrentMonthYear"
        @changeSelectedDates="changeSelectedDates" />
    </div>

    <!-- Footer Button -->
    <div
      v-if="!props.helperCalendar"
      class="pb-2 shadow-md md:relative md:p-0 md:shadow-none">
      <div
        class="my-2 flex flex-wrap place-content-center text-gc-12 leading-6">
        <CalendarLegend
          v-if="!isCalendarView || !isMobile"
          :items="CalendarLegendOptions"
          divClass="flex items-center px-3 text-nowrap"
          spanClass="rounded-full h-2.5 w-2.5 mr-1.5" />
      </div>
      <WarningInfo
        :class="'hidden md:flex md:justify-center'"
        :textClass="'pt-1'"
        :text="$t('calendarRatesInfoNote', { symbol: currencySymbol })" />
      <hr class="mt-5 md:hidden" />

      <Button
        :class="'h-14 w-full md:hidden'"
        @click="confirmDateSelection"
        :label="$t('done')" />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
  defineAsyncComponent,
} from 'vue';
import {
  differenceInMonths,
  startOfMonth,
  addDays,
  addMonths,
  format,
  getMonth,
  getYear,
  differenceInDays,
} from 'date-fns';
import { useRouter } from 'vue-router';
const VueDatePicker = defineAsyncComponent(
  () => import('guestcentric-vue-datepicker'),
);
import 'guestcentric-vue-datepicker/dist/main.css';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import { useScreenSize } from '@composables/useScreenSize';
import { getWeekNames } from '@composables/useFormattedDate';

const Button = defineAsyncComponent(
  () => import('@/components/customComponents/CustomButton.vue'),
);
const DatesSelection = defineAsyncComponent(
  () => import('@/components/Calendar/DatesSelection.vue'),
);
const WarningInfo = defineAsyncComponent(
  () => import('@/components/Calendar/WarningInfo.vue'),
);
const CalendarLegend = defineAsyncComponent(
  () => import('@/components/Calendar/GlobalLegend.vue'),
);
const IconButton = defineAsyncComponent(
  () => import('@/components/customComponents/IconButton.vue'),
);
import ChartBarIcon from '@icons/chart-bar.svg';
import ChevronIcon from '@icons/chevron-right.svg';
import CalendarDateRange from '@icons/calendar-date-range.svg';

const GraphChart = defineAsyncComponent(
  () => import('@/components/BarChart/GraphChart.vue'),
);

import { useRatesStore } from '@stores/rates.store';
import { useAppConfigStore } from '@stores/appConfig.store';

const appConfigStore = useAppConfigStore();
const { applicationSetup } = storeToRefs(appConfigStore);
const ratesStore = useRatesStore();
const datepicker = ref(null);
const { getBar } = ratesStore;
const { ratesData } = storeToRefs(ratesStore);

const { isMobile } = useScreenSize();
const { locale } = useI18n();

const emit = defineEmits(['datesSelected', 'setDatesSelected', 'cancel']);
const props = defineProps({
  initialStartDate: Date,
  initialEndDate: Date,
  maxDate: String,
  helperCalendar: {
    type: Boolean,
    default: false,
  },
  showBar: {
    type: Boolean,
    default: true,
  },
  currency: String,
  currencySymbol: String,
  focusOn: String,
});

const CalendarLegendOptions = [
  {
    title: 'noAvailability',
    color: 'bg-error',
  },
];

const router = useRouter();

const HasTooltip = (date, dateC) => {
  if (dateC === '') {
    return false;
  }
  const formattedDate = format(date, 'yyyy-MM-dd');
  const formattedCalendarDate = format(dateC, 'yyyy-MM-dd');
  return formattedCalendarDate === formattedDate;
};

const selectedDates = ref();
const numberOfMonths = ref(3);
const maxNumberOfMonths = ref(3);

const minSelectableDate = ref();
const maxSelectableDate = ref();

const isCalendarView = ref(true);
const startDate = ref();
const endDate = ref();
const visibleMonths = ref(1);
const timezone = ref('');

const calendarStartDate = computed(() => {
  return isMobile.value ? minSelectableDate.value : startDate.value;
});

onMounted(() => {
  timezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  minSelectableDate.value = new Date(
    applicationSetup.value.default_checkin_date + 'T00:00:00',
  );
  maxSelectableDate.value = new Date(props.maxDate + 'T00:00:00');
  startDate.value = props.initialStartDate;
  endDate.value = props.initialEndDate;
  let month = getMonth(new Date(startDate.value));
  let year = getYear(new Date(startDate.value));

  maxNumberOfMonths.value =
    differenceInMonths(
      startOfMonth(maxSelectableDate.value),
      startOfMonth(minSelectableDate.value),
    ) + 1;

  let monthsDiff = calculateMonthsDiff(
    startDate.value,
    minSelectableDate.value,
  );

  if (monthsDiff > 3) {
    numberOfMonths.value = Math.min(monthsDiff + 3, maxNumberOfMonths.value);
  }

  if (
    getMonth(new Date(startDate.value)) !== getMonth(new Date(endDate.value))
  ) {
    visibleMonths.value = 2;
  }

  getBar(month, year)
    .then(() => {
      const firstMonth = getMonth(minSelectableDate.value);
      const firstYear = getYear(minSelectableDate.value);
      if (firstMonth !== month || firstYear !== year) {
        getBar(firstMonth, firstYear, monthsDiff - 2); // current and initial month
      }
    })
    .catch((error) => {
      console.error('Error fetching rates data', error);
    });

  selectedDates.value = [startDate.value, endDate.value];
  if (isMobile.value) {
    setTimeout(() => {
      positionCalendars();
    }, 10);
  }
});

watch(
  () => [props.initialStartDate, props.initialEndDate],
  (newDates) => {
    selectedDates.value = newDates;
  },
);

watch(
  () => props.currency,
  () => {
    let month = getMonth(new Date(startDate.value));
    let year = getYear(new Date(startDate.value));
    let monthsDiff = calculateMonthsDiff(
      startDate.value,
      minSelectableDate.value,
    );
    if (currentMonthYear.value.year) {
      let visibleMonth = format(
        new Date(currentMonthYear.value.year, currentMonthYear.value.month, 1),
        'yyyy-MM-dd',
      );
      visibleMonth = format(addMonths(new Date(visibleMonth), 2), 'yyyy-MM-dd');
      if (visibleMonth > startDate.value) {
        monthsDiff = calculateMonthsDiff(visibleMonth, minSelectableDate.value);
        month = getMonth(new Date(visibleMonth));
        year = getYear(new Date(visibleMonth));
      }
      monthsDiff = Math.max(
        calculateMonthsDiff(visibleMonth, minSelectableDate.value),
        monthsDiff,
      );
    }
    getBar(month, year)
      .then(() => {
        const firstMonth = getMonth(minSelectableDate.value);
        const firstYear = getYear(minSelectableDate.value);
        if (firstMonth !== month || firstYear !== year) {
          getBar(firstMonth, firstYear, monthsDiff - 2);
        }
      })
      .catch((error) => {
        console.error('Error fetching rates data', error);
      });
  },
);

watch(
  () => props.focusOn,
  (focusOnStr) => {
    if (focusOnStr && startDate.value && endDate.value) {
      let targetDate =
        focusOnStr === 'startDate' ? startDate.value : endDate.value;
      targetDate = format(targetDate, 'yyyy-MM-dd');
      let [year, month] = targetDate.split('-').map(Number);
      month -= 1;

      let currentMonth = currentMonthYear.value.month;
      let currentYear = currentMonthYear.value.year;

      if (
        (currentYear === year && currentMonth === month) ||
        (currentYear === year && currentMonth + 1 === month) ||
        (currentYear === year - 1 && currentMonth === 11 && month === 0)
      ) {
        return;
      }

      datepicker.value.setMonthYear({ month, year });
    }
  },
);

const calculateMonthsDiff = (startD, minSelectableD) => {
  return (
    differenceInMonths(startOfMonth(startD), startOfMonth(minSelectableD)) + 1
  );
};

const positionCalendars = () => {
  const dp = document.querySelector('.dp__calendar');
  if (!dp) return;

  let selected = document.querySelector('.dp__range_start');

  if (selectedDates.value[0]) {
    const month = getMonth(selectedDates.value[0]);
    const year = getYear(selectedDates.value[0]);
    selected = document.querySelector('.cal-' + month + '-' + year);
  }

  if (selected && selected.scrollIntoView) {
    selected.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const onCalendarScroll = ({
  target: { scrollTop, clientHeight, scrollHeight },
}) => {
  if (props.helperCalendar) {
    return;
  }
  if (scrollTop + 400 + clientHeight >= scrollHeight) {
    const newDate = addMonths(
      new Date(minSelectableDate.value),
      numberOfMonths.value - 1,
    );
    getBar(getMonth(newDate), getYear(newDate));
    numberOfMonths.value = Math.min(
      numberOfMonths.value + 3,
      maxNumberOfMonths.value,
    );
  }
};
const isNavDisabled = (month, year, isNext) => {
  if (isNext) {
    return (
      differenceInMonths(
        startOfMonth(new Date(year, month, 1)),
        startOfMonth(maxSelectableDate.value),
      ) >= 0
    );
  }
  let firstMonth = month - 1;
  if (props.helperCalendar) {
    firstMonth = month;
  }
  let firstYear = year;
  if (month == 0 && !props.helperCalendar) {
    firstMonth = 11;
    firstYear = year - 1;
  }
  return (
    differenceInMonths(
      startOfMonth(minSelectableDate.value),
      startOfMonth(new Date(firstYear, firstMonth, 1)),
    ) >= 0
  );
};

const emitDatesSelected = () => {
  emit('datesSelected', [startDate.value, endDate.value]);
};

const changeSelectedDates = (dates) => {
  startDate.value = dates[0];
  endDate.value = dates[1];
  if (startDate.value && endDate.value) {
    updateUrlWithDates(startDate.value, endDate.value);
    emitDatesSelected();
    if (!isMobile.value) {
      confirmDateSelection();
    }
    return;
  }
  emitDatesSelected();
};

const confirmDateSelection = () => {
  if (endDate.value === '') {
    endDate.value = addDays(startDate.value, 1);
    emit('datesSelected', [startDate.value, endDate.value]);
    updateUrlWithDates(startDate.value, endDate.value);
  }
  emit('setDatesSelected');
};

const toggleViewMode = () => {
  isCalendarView.value = !isCalendarView.value;
  if (isCalendarView.value) {
    setTimeout(() => {
      if (isMobile.value) {
        selectedDates.value = [startDate.value, endDate.value];
      }
      positionCalendars();
    }, 10);
  }
};

const dayNames = computed(() => getWeekNames('narrow').weekDays);

const multiCalendarsC = computed(() => {
  if (!props.helperCalendar) {
    return { count: isMobile.value ? numberOfMonths.value : 2 };
  } else if (visibleMonths.value > 1) {
    return { count: visibleMonths.value };
  }
  return false;
});

const onRangeStart = (value) => {
  startDate.value = value;
  endDate.value = '';
  selectedDates.value = [startDate.value, endDate.value];
  nextTick(() => {
    visibleMonths.value = 2;
    emitDatesSelected();
  });
};

const getDayRate = (date) => {
  const formattedDate = format(date, 'yyyy-MM-dd');
  let rate = 'x';
  if (ratesData.value.bar[formattedDate]) {
    rate = parseInt(ratesData.value.bar[formattedDate]);
  }
  return rate;
};

const getDayCellClass = (date) => {
  const formattedDate = format(date, 'yyyy-MM-dd');
  if (
    ratesData.value.unavailable_days.find(
      (unavailableDate) => unavailableDate === formattedDate,
    )
  ) {
    return 'marked-cell-unavailable';
  }
  return '';
};

const currentMonthYear = ref({
  month:
    parseInt(format(props.initialStartDate, 'yyyy-MM-dd').split('-')[1]) - 1,
  year: parseInt(format(props.initialStartDate, 'yyyy-MM-dd').split('-')[0]),
});

const handleMonthYear = (object) => {
  let firstMonth = object.month;
  let firstYear = object.year;
  if (object.instance == 1) {
    firstMonth = object.month - 1;
    if (object.month == 0) {
      firstMonth = 11;
      firstYear = object.year - 1;
    }
  }
  currentMonthYear.value = { month: firstMonth, year: firstYear };
  getBar(firstMonth, firstYear);
};

const changeCurrentMonthYear = (object) => {
  let currentMonth = object.month;
  let currentYear = object.year;
  currentMonthYear.value = { month: currentMonth, year: currentYear };
};

const updateUrlWithDates = (startDate, endDate) => {
  const nrNights = differenceInDays(endDate, startDate);
  const currentQuery = router.currentRoute.value.query;
  const updatedQuery = {
    ...currentQuery,
    startDay: format(startDate, 'yyyy-MM-dd'),
    nrNights: nrNights,
  };
  router.push({ query: updatedQuery });
};

const handleDate = (val) => {
  startDate.value = val[0];
  let checkoutDate = val[1];
  if (
    format(startDate.value, 'yyyy-MM-dd') === format(checkoutDate, 'yyyy-MM-dd')
  ) {
    checkoutDate = addDays(startDate.value, 1);
  }
  endDate.value = checkoutDate;
  if (
    getMonth(new Date(startDate.value)) !== getMonth(new Date(endDate.value))
  ) {
    visibleMonths.value = 2;
  } else {
    visibleMonths.value = 1;
  }

  selectedDates.value = [startDate.value, endDate.value];
  nextTick(() => {
    emitDatesSelected();
    updateUrlWithDates(startDate.value, endDate.value);
    if (!isMobile.value) {
      confirmDateSelection();
    }
  });
};
</script>
<style>
.dp__menu_inner .dp__instance_calendar:nth-of-type(1) .dp--header-wrap button {
  @apply hidden;
}
:not(.dp__cell_disabled).marked-cell-unavailable {
  @apply bg-error text-light;
}
:not(.dp__cell_disabled).marked-cell-unavailable:hover {
  @apply bg-error text-light;
}
.dp__calendar_row {
  @apply grid grid-cols-7 gap-px;
  @apply my-0.5;
}
.dp__main > div:nth-child(2) {
  @apply w-full;
}
.dp__cell_inner {
  @apply flex aspect-square h-auto w-auto flex-col items-stretch justify-center border-0;
}
.dp__menu {
  @apply border-0;
}
.dp__calendar_header_separator {
  @apply hidden;
}
.dp__calendar_header_item {
  @apply font-cal text-base font-medium leading-4;
}
.dp__menu_inner {
  @apply p-0;
}
.dp__calendar_next {
  margin-inline-start: 0;
}
.isMobile .dp__flex_display,
.dp__vertical_display .dp__flex_display {
  @apply flex-col;
}
.dp__vertical_display .dp__menu_inner > :not([hidden]) ~ :not([hidden]) {
  @apply ml-0;
}
.dp__vertical_display
  .dp__menu_inner
  .dp__instance_calendar:nth-of-type(1)
  .dp--header-wrap
  button {
  @apply flex;
}
.dp__vertical_display
  .dp__menu_inner
  .dp__instance_calendar:nth-of-type(2)
  .dp--header-wrap
  button {
  @apply hidden;
}
.isMobile .dp__month_year_wrap > div {
  @apply md:text-center;
}
.isMobile .dp__month_year_wrap > div > div {
  @apply md:hidden;
}
@media only screen and (width >1024px) {
  .dp__menu_inner {
    @apply space-x-5;
  }
}
.dp__theme_light {
  --dp-primary-color: var(--color-cal-select);
  --dp-range-between-dates-background-color: var(--color-cal-select-betwn);
  --dp-cell-border-radius: var(--border-radius-sm);
  /*--dp-font-size: var(--font-primary);*/
  --dp-background-color: transparent;
}

.marked-cell-unavailable i {
  @apply text-light;
}

.dp__range_between {
  @apply text-cal-select-betwn-text;
}

.dp__range_between i {
  @apply text-cal-select-betwn-prices-text;
}

.dp__range_start,
.dp__range_start i,
.dp__range_end {
  @apply text-cal-select-text;
}

.cal-tooltip {
  @apply hidden;
}

.dp__range_start .cal-tooltip,
.dp__range_end .cal-tooltip {
  @apply absolute bottom-full left-1/2 mb-1 flex w-max -translate-x-1/2 transform rounded bg-brand px-2 py-1 text-base text-light opacity-0 shadow-lg transition-opacity duration-300 group-hover:block group-hover:opacity-100;
  @apply before:absolute before:left-1/2 before:top-full before:h-0 before:w-0 before:-translate-x-1/2 before:border-l-4 before:border-r-4 before:border-t-4 before:border-transparent before:border-t-brand before:content-[''];
}

.startDate-tooltip .cal-start-tooltip,
.endDate-tooltip .cal-end-tooltip {
  @apply opacity-100;
}

.dp__range_end i {
  @apply invisible;
}

.dp__cell_disabled i {
  @apply hidden;
}

.dp__cell_inner {
  @apply font-cal;
}

.dp__cell_inner > span {
  @apply mb-1.5 sm:mb-3 md:mb-1 lg:mb-2;
}

.dp__vertical_display:not(.isMobile) .dp__cell_inner > span {
  @apply mb-1;
}

.dp--header-wrap {
  @apply mt-3;
}

.dp__calendar {
  @apply mb-6;
}
.dp__cell_disabled.dp__date_hover {
  @apply bg-transparent;
  color: var(--dp-secondary-color);
}
.dp__instance_calendar:last-of-type .dp__calendar {
  @apply mb-0;
}
</style>
